import { PiniaColada } from '@pinia/colada';
import datadogPlugin from '@/plugins/datadog';
import { createApp } from 'vue';
import App from '@/App.vue';
import router from '@/router';
import pinia from '@/store';
import vuetify from '@/plugins/vuetify';
import logger from '@/services/logger';
import error from '@/services/errors';
import { createAuth0 } from '@auth0/auth0-vue';
import VueSweetalert2 from 'vue-sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import LocalDevDomainNotice from '@/components/common/LocalDevDomainNotice.vue';
import MissingEnvNotice from '@/components/common/MissingEnvNotice.vue';
import { getAppUrl } from '@/utils/appEnv';
import { getMissingAuth0EnvVars, hasRequiredAuth0Env } from '@/utils/authEnv';
import '@/assets/global.css';
import '@/assets/fontawesome/css/all.css';
import { usePoTrackerStore } from '@/store/poTrackerStore';

datadogPlugin.install();

const currentDomain = window.location.hostname;

function isLegacyLocalDevHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

if (import.meta.env.DEV && isLegacyLocalDevHost(currentDomain)) {
  createApp(LocalDevDomainNotice).use(vuetify).mount('#app');
} else if (!hasRequiredAuth0Env()) {
  createApp(MissingEnvNotice, {
    missingVariables: getMissingAuth0EnvVars(),
  })
    .use(vuetify)
    .mount('#app');
} else {
  const app = createApp(App);
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const cookieDomain = import.meta.env.VITE_AUTH0_COOKIE_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
  const redirectUri = window.location.origin;

  app.config.globalProperties.$log = logger;
  app.config.globalProperties.$error = error;

  app.use(pinia);
  app.use(PiniaColada);
  // Created outside any component on purpose: see the note at the top of the store.
  usePoTrackerStore();
  app.use(vuetify);
  app.use(VueSweetalert2);
  app.use(router);
  app.use(
    createAuth0({
      domain,
      clientId,
      cookieDomain,
      authorizationParams: {
        redirect_uri: redirectUri,
        audience: audience,
      },
    })
  );

  if (import.meta.env.DEV) {
    logger.info(`MP Portal running at ${getAppUrl('local')}`);
  }

  app.mount('#app');
}
